import { Store } from "vuex";
import { DocumentTypeEnum } from "@/enums/DocumentTypeEnum";
import { getDocumentName, isImage } from "@/helpers/documents";
import { downloadFile } from "@/helpers/downloadFile";
import {
  isValidDocumentType,
  isValidFileSize,
  validDocumentsTypes,
} from "@/helpers/fileUpload";
import { State } from "@/store";
import { BuilderInterfaceWithHouseDesigns } from "@/types/BuilderInterface";
import { EstateInterface } from "@/types/EstateInterface";
import { LotDocument, LotInterface } from "@/types/LotInterface";

type IdValue = string | { _id?: string } | null | undefined;

export const acceptedBuilderDocumentTypes = validDocumentsTypes.join(", ");

export function getId(value: IdValue): string {
  return typeof value === "string" ? value : value?._id ?? "";
}

export function getSelectedBuilderId(lot: LotInterface): string {
  return getId(lot.builders?.[0]?.builderId);
}

export function getBuilderPayload(
  _builders: BuilderInterfaceWithHouseDesigns[],
  builderId: string
) {
  return {
    builderId,
    housesIds: [],
  };
}

export function getEstateLandownerIds(
  estate?: EstateInterface | null
): string[] {
  return [
    getId(estate?.landownerId),
    ...(estate?.collaboratorIds ?? []),
  ].filter(Boolean);
}

export async function uploadBuilderDocument(
  store: Store<State>,
  file: File,
  lotId: string,
  estateId: string
): Promise<LotDocument | null> {
  if (!isValidDocumentType(file) || !isValidFileSize(file)) return null;

  const data = new FormData();

  data.append("file", file);
  data.append("documentType", DocumentTypeEnum.BUILDER_DOCUMENTS);
  data.append("lotId", lotId);
  data.append("estateId", estateId);

  const response = await store.dispatch("lot/uploadLotDocuments", data);

  return response.data;
}

export function deleteBuilderDocument(
  store: Store<State>,
  lotId: string,
  fileName: string
): Promise<unknown> {
  return store.dispatch("lot/deleteLotDocument", {
    lotId,
    fileName,
    documentType: DocumentTypeEnum.BUILDER_DOCUMENTS,
  });
}

export function openBuilderDocument(document: LotDocument): void {
  if (isImage(document.fileName)) {
    window.open(document.link, "_blank");
    return;
  }

  downloadFile(document.link);
}

export { getDocumentName };
